import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../ui/icon/icon.component';
import { SkeletonComponent } from '../ui/skeleton/skeleton.component';
import {
  DEFAULT_MEDIA_VIEWER_CONFIG,
  MEDIA_VIEWER_CONFIG,
  MediaViewerConfig,
} from './media-viewer.config';
import {
  MediaFile,
  MediaFitMode,
  MediaSortField,
  MediaTheme,
  MediaTypeFilter,
  MediaViewerError,
  MediaViewStyle,
} from './models/media.model';
import { MediaDownloadService } from './services/media-download.service';
import { MediaStateService } from './services/media-state.service';
import { MediaZoomService } from './services/media-zoom.service';
import { ThumbnailService } from './services/thumbnail.service';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

/** Playback speeds cycled by the lightbox video speed button. */
const PLAYBACK_RATES = [0.5, 1, 1.25, 1.5, 2] as const;

/**
 * One control in the lightbox toolbar. Declared once as data (see `lightboxTools`) and rendered by a
 * single `@for`, so adding/reordering a tool never means copy-pasting button markup. `icon`/`title`/
 * `ariaLabel` are getters so a tool can react to state (fullscreen ⇄ windowed, light ⇄ dark).
 */
interface LightboxTool {
  readonly id: string;
  readonly kind: 'button' | 'divider' | 'zoom';
  readonly icon?: () => string;
  readonly title?: () => string;
  readonly ariaLabel?: () => string;
  readonly action?: () => void;
  readonly visible: () => boolean;
}

/**
 * Reusable, enterprise-grade image + video gallery with an integrated lightbox, built with
 * Kendo-consistent chrome. Presentational: it takes a `MediaFile[]` and emits intent — fetching,
 * deleting and uploading stay with the host (a mock service now, a real API later).
 *
 * All reactive state lives in the per-instance `MediaStateService` (provided below), so several
 * viewers can coexist. Scroll/gesture work runs outside Angular and re-enters the zone only for the
 * state writes that must trigger change detection — the same discipline as `<app-pdf-viewer>`.
 */
@Component({
  selector: 'app-media-viewer',
  standalone: true,
  imports: [CommonModule, IconComponent, SkeletonComponent, NgbTooltipModule],
  templateUrl: './media-viewer.component.html',
  styleUrl: './media-viewer.component.scss',
  providers: [MediaStateService],
  host: {
    class: 'media-viewer',
    '[class.media-viewer--dark]': "state.theme() === 'dark'",
    '[class.media-viewer--lightbox-open]': 'state.lightboxOpen()',
    '[class.media-viewer--headless]': '!showGallery',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaViewerComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private readonly zone = inject(NgZone);
  private readonly zoomSvc = inject(MediaZoomService);
  private readonly downloadSvc = inject(MediaDownloadService);
  private readonly thumbnails = inject(ThumbnailService);
  private readonly destroyRef = inject(DestroyRef);

  /** Public reactive store — read by the template. */
  readonly state = inject(MediaStateService);

  private readonly config: MediaViewerConfig = {
    ...DEFAULT_MEDIA_VIEWER_CONFIG,
    ...(inject(MEDIA_VIEWER_CONFIG, { optional: true }) ?? {}),
  };

  // ---- Inputs (per-instance overrides of the global config) --------------------------------
  @Input() items: readonly MediaFile[] = [];
  @Input() loading = false;
  @Input() emptyText = 'No media to display.';
  /**
   * Hide the gallery chrome (cards / toolbar / pager) and render only the lightbox overlay. Use it
   * as a "headless" trigger: place your own image/thumbnail on the page and call `open()` on click.
   */
  @Input() showGallery = true;
  @Input() view: MediaViewStyle = this.config.view;
  @Input() theme: MediaTheme = this.config.theme;
  @Input() fitMode: MediaFitMode = this.config.fitMode;
  @Input() minZoom = this.config.minZoom;
  @Input() maxZoom = this.config.maxZoom;
  @Input() pageSize = this.config.pageSize;
  @Input() showToolbar = this.config.showToolbar;
  @Input() showSearch = this.config.showSearch;
  @Input() showFilter = this.config.showFilter;
  @Input() showSort = this.config.showSort;
  @Input() showViewToggle = this.config.showViewToggle;
  @Input() showUpload = this.config.showUpload;
  @Input() showRefresh = this.config.showRefresh;
  @Input() showDelete = false;
  @Input() showZoom = this.config.showZoom;
  @Input() showRotate = this.config.showRotate;
  @Input() showDownload = this.config.showDownload;
  @Input() showPrint = this.config.showPrint;
  @Input() showFullscreen = this.config.showFullscreen;
  @Input() showThumbnails = this.config.showThumbnails;
  @Input() showTheme = this.config.showTheme;
  @Input() showFavorite = false;
  @Input() enableKeyboardNavigation = this.config.enableKeyboardNavigation;
  @Input() enableWheelZoom = this.config.enableWheelZoom;
  @Input() enableSwipe = this.config.enableSwipe;

  // ---- Outputs (named to avoid shadowing native DOM events) --------------------------------
  @Output() selected = new EventEmitter<MediaFile>();
  @Output() opened = new EventEmitter<MediaFile>();
  @Output() closed = new EventEmitter<void>();
  @Output() indexChanged = new EventEmitter<number>();
  @Output() downloaded = new EventEmitter<MediaFile>();
  @Output() printed = new EventEmitter<MediaFile>();
  @Output() deleted = new EventEmitter<MediaFile>();
  @Output() favoriteToggled = new EventEmitter<MediaFile>();
  @Output() uploadRequested = new EventEmitter<void>();
  @Output() refreshRequested = new EventEmitter<void>();
  @Output() viewChanged = new EventEmitter<MediaViewStyle>();
  @Output() errored = new EventEmitter<MediaViewerError>();

  @ViewChild('stage') private readonly stage?: ElementRef<HTMLElement>;
  @ViewChild('lightbox') private readonly lightbox?: ElementRef<HTMLElement>;
  @ViewChild('video') private readonly videoEl?: ElementRef<HTMLVideoElement>;
  @ViewChildren('card') private readonly cards?: QueryList<ElementRef<HTMLElement>>;

  /** Lazily-captured video poster frames, keyed by media id. */
  readonly videoThumbs = signal<Record<string, string>>({});
  readonly playbackRate = signal(1);
  readonly panX = signal(0);
  readonly panY = signal(0);

  /** Static option lists for the toolbar segments. */
  readonly typeOptions: { readonly value: MediaTypeFilter; readonly label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'image', label: 'Images' },
    { value: 'video', label: 'Videos' },
  ];
  readonly sortOptions: { readonly value: MediaSortField; readonly label: string }[] = [
    { value: 'name', label: 'Name' },
    { value: 'date', label: 'Date' },
    { value: 'size', label: 'Size' },
    { value: 'type', label: 'Type' },
  ];
  /** Placeholder count for the skeleton grid while `loading`. */
  readonly skeletons = Array.from({ length: 8 });

  /** Lightbox toolbar, declared as data and rendered by one `@for` in the template. */
  readonly lightboxTools: readonly LightboxTool[] = [
    {
      id: 'zoom-out',
      kind: 'button',
      icon: () => 'zoomOut',
      title: () => 'Zoom out',
      ariaLabel: () => 'Zoom out',
      action: () => this.zoomOut(),
      visible: () => this.showZoom && this.state.isImageActive(),
    },
    {
      id: 'zoom-level',
      kind: 'zoom',
      visible: () => this.showZoom && this.state.isImageActive(),
    },
    {
      id: 'zoom-in',
      kind: 'button',
      icon: () => 'zoomIn',
      title: () => 'Zoom in',
      ariaLabel: () => 'Zoom in',
      action: () => this.zoomIn(),
      visible: () => this.showZoom && this.state.isImageActive(),
    },
    {
      id: 'fit-screen',
      kind: 'button',
      icon: () => 'fitScreen',
      title: () => 'Fit to screen',
      ariaLabel: () => 'Fit to screen',
      action: () => this.fitToScreen(),
      visible: () => this.showZoom && this.state.isImageActive(),
    },
    {
      id: 'actual-size',
      kind: 'button',
      icon: () => 'actualSize',
      title: () => 'Original size',
      ariaLabel: () => 'Original size',
      action: () => this.actualSize(),
      visible: () => this.showZoom && this.state.isImageActive(),
    },
    {
      id: 'divider-rotate',
      kind: 'divider',
      visible: () => this.showRotate && this.state.isImageActive(),
    },
    {
      id: 'rotate-left',
      kind: 'button',
      icon: () => 'rotateLeft',
      title: () => 'Rotate left',
      ariaLabel: () => 'Rotate left',
      action: () => this.rotateLeft(),
      visible: () => this.showRotate && this.state.isImageActive(),
    },
    {
      id: 'rotate-right',
      kind: 'button',
      icon: () => 'rotateRight',
      title: () => 'Rotate right',
      ariaLabel: () => 'Rotate right',
      action: () => this.rotateRight(),
      visible: () => this.showRotate && this.state.isImageActive(),
    },
    {
      id: 'divider-actions',
      kind: 'divider',
      visible: () => this.state.isImageActive() && (this.showZoom || this.showRotate),
    },
    {
      id: 'download',
      kind: 'button',
      icon: () => 'download',
      title: () => 'Download',
      ariaLabel: () => 'Download',
      action: () => this.download(),
      visible: () => this.showDownload,
    },
    {
      id: 'print',
      kind: 'button',
      icon: () => 'print',
      title: () => 'Print',
      ariaLabel: () => 'Print',
      action: () => this.print(),
      visible: () => this.showPrint && this.state.isImageActive(),
    },
    {
      id: 'fullscreen',
      kind: 'button',
      icon: () => (this.state.fullscreen() ? 'minimize' : 'maximize'),
      title: () => (this.state.fullscreen() ? 'Exit fullscreen' : 'Fullscreen'),
      ariaLabel: () => (this.state.fullscreen() ? 'Exit fullscreen' : 'Enter fullscreen'),
      action: () => this.toggleFullscreen(),
      visible: () => this.showFullscreen,
    },
    {
      id: 'theme',
      kind: 'button',
      icon: () => (this.state.theme() === 'dark' ? 'sun' : 'moon'),
      title: () => (this.state.theme() === 'dark' ? 'Light theme' : 'Dark theme'),
      ariaLabel: () => 'Toggle colour theme',
      action: () => this.toggleTheme(),
      visible: () => this.showTheme,
    },
    {
      id: 'divider-close',
      kind: 'divider',
      visible: () => true,
    },
    {
      id: 'close',
      kind: 'button',
      icon: () => 'x',
      title: () => 'Close (Esc)',
      ariaLabel: () => 'Close viewer',
      action: () => this.close(),
      visible: () => true,
    },
  ];

  private observer?: IntersectionObserver;
  private lastFocused: HTMLElement | null = null;
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private pinchStartDistance = 0;
  private pinchStartZoom = 100;
  private panStart = { x: 0, y: 0, ox: 0, oy: 0 };
  private panning = false;
  private swipeStartX = 0;

  // ---- Lifecycle ----------------------------------------------------------------------------
  ngOnInit(): void {
    this.state.setTheme(this.theme);
    this.state.setView(this.view);
    this.state.setFitMode(this.fitMode);
    this.state.setPageSize(this.pageSize);
    this.state.setItems(this.items);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.state.setItems(this.items);
    }
    if (changes['theme'] && !changes['theme'].firstChange) {
      this.state.setTheme(this.theme);
    }
    if (changes['view'] && !changes['view'].firstChange) {
      this.state.setView(this.view);
    }
    if (changes['pageSize'] && !changes['pageSize'].firstChange) {
      this.state.setPageSize(this.pageSize);
    }
  }

  ngAfterViewInit(): void {
    this.setupThumbObserver();
    this.cards?.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.observeCards());
    this.observeCards();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  // ---- Toolbar: search / filter / sort / view ----------------------------------------------
  onSearch(value: string): void {
    this.state.setSearch(value);
  }

  onTypeFilter(value: MediaTypeFilter): void {
    this.state.setTypeFilter(value);
  }

  toggleExtension(extension: string): void {
    const current = new Set(this.state.extensions());
    if (current.has(extension)) {
      current.delete(extension);
    } else {
      current.add(extension);
    }
    this.state.setExtensions([...current]);
  }

  onSortField(field: MediaSortField): void {
    this.state.setSortField(field);
  }

  toggleView(): void {
    this.viewChanged.emit(this.state.toggleView());
  }

  toggleTheme(): void {
    this.state.toggleTheme();
  }

  onRefresh(): void {
    this.refreshRequested.emit();
  }

  onUpload(): void {
    this.uploadRequested.emit();
  }

  goToPage(page: number): void {
    this.state.setPage(page);
  }

  // ---- Cards --------------------------------------------------------------------------------
  onCardActivate(file: MediaFile): void {
    const index = this.state.filtered().findIndex((item) => item.id === file.id);
    this.selected.emit(file);
    if (index >= 0) {
      this.open(index);
    }
  }

  /**
   * Open the lightbox at `index` (default: the first item). Public so a host can trigger the full
   * viewer from its own element — e.g. a single inline image with `(click)="viewer.open()"`.
   */
  open(index = 0): void {
    const item = this.state.filtered()[index];
    if (!item) {
      return;
    }
    this.resetPan();
    this.state.openLightbox(index);
    this.opened.emit(item);
    queueMicrotask(() => this.focusLightbox());
  }

  /** Open the lightbox at the item matching `id`. */
  openById(id: string): void {
    const index = this.state.filtered().findIndex((item) => item.id === id);
    if (index >= 0) {
      this.open(index);
    }
  }

  onCardKeydown(event: KeyboardEvent, file: MediaFile): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onCardActivate(file);
    }
  }

  onFavorite(event: Event, file: MediaFile): void {
    event.stopPropagation();
    this.favoriteToggled.emit(file);
  }

  onDelete(event: Event, file: MediaFile): void {
    event.stopPropagation();
    this.deleted.emit(file);
  }

  /** Best thumbnail for a card: pre-generated, image src, captured video frame, else icon. */
  thumbFor(file: MediaFile): string | null {
    if (file.type === 'video') {
      return file.thumbnailUrl ?? this.videoThumbs()[file.id] ?? null;
    }
    return this.thumbnails.resolve(file);
  }

  // ---- Lightbox navigation ------------------------------------------------------------------
  close(): void {
    this.state.closeLightbox();
    this.closed.emit();
    this.exitFullscreen();
    this.lastFocused?.focus?.();
  }

  next(): void {
    this.resetPan();
    this.state.next();
    this.emitIndex();
  }

  previous(): void {
    this.resetPan();
    this.state.previous();
    this.emitIndex();
  }

  private emitIndex(): void {
    this.playbackRate.set(1);
    this.indexChanged.emit(this.state.activeIndex());
  }

  /** Thumbnail-strip click already moved the index; just reset transient view state and notify. */
  emitIndexFromStrip(): void {
    this.resetPan();
    this.emitIndex();
  }

  // ---- Lightbox image tools -----------------------------------------------------------------
  zoomIn(): void {
    this.zoomTo(this.zoomSvc.zoomIn(this.state.zoom(), this.config.zoomStep, this.maxZoom));
  }

  zoomOut(): void {
    this.zoomTo(this.zoomSvc.zoomOut(this.state.zoom(), this.config.zoomStep, this.minZoom));
  }

  zoomTo(zoom: number): void {
    const clamped = this.zoomSvc.clamp(zoom, this.minZoom, this.maxZoom);
    if (clamped === 100) {
      this.resetPan();
    }
    this.state.setZoom(clamped);
  }

  fitToScreen(): void {
    this.state.setFitMode('contain');
    this.resetPan();
    this.zoomTo(100);
  }

  actualSize(): void {
    this.state.setFitMode('actual');
    const item = this.state.activeItem();
    const stage = this.stage?.nativeElement;
    if (item?.width && item.height && stage) {
      // Use an effectively-unclamped floor (1) here: the min-zoom clamp is for what the user can
      // dial to, but the natural-size ratio must come from the true fit scale, or very large images
      // (fit ratio < minZoom) land at the wrong "actual size". zoomTo() applies the real bounds.
      const fit = this.zoomSvc.fitToZoom(
        { width: item.width, height: item.height },
        { width: stage.clientWidth, height: stage.clientHeight },
        this.state.rotation(),
        1,
        this.maxZoom
      );
      const natural = fit > 0 ? Math.round((100 / fit) * 100) : 100;
      this.zoomTo(natural);
    } else {
      this.zoomTo(100);
    }
  }

  rotateLeft(): void {
    this.state.rotate('left');
  }

  rotateRight(): void {
    this.state.rotate('right');
  }

  rotationStyle(): string {
    const rotation = this.state.rotation();
    const scale = this.zoomSvc.toScale(this.state.zoom());
    return `translate(${this.panX()}px, ${this.panY()}px) rotate(${rotation}deg) scale(${scale})`;
  }

  // ---- Media element events -----------------------------------------------------------------
  onMediaLoaded(): void {
    this.state.setMediaLoaded();
  }

  onMediaError(): void {
    const item = this.state.activeItem();
    const error: MediaViewerError = {
      code: item && !this.isPlayable(item) ? 'unsupported' : 'render',
      message: item
        ? `Unable to display "${item.name}".`
        : 'Unable to display this media.',
    };
    this.state.setMediaError(error);
    this.errored.emit(error);
  }

  /** Cache of codec-support probes, keyed by extension — this is called from the template on every
   * change-detection pass, so the result must not allocate a fresh <video> element each time. */
  private readonly playableByExtension = new Map<string, boolean>();

  /** Whether the browser can play this video's container/codec at all. */
  isPlayable(file: MediaFile): boolean {
    if (file.type !== 'video') {
      return true;
    }
    const key = file.extension.toLowerCase();
    const cached = this.playableByExtension.get(key);
    if (cached !== undefined) {
      return cached;
    }
    const probe = document.createElement('video');
    const mime = this.videoMime(file.extension);
    const playable = mime ? probe.canPlayType(mime) !== '' : false;
    this.playableByExtension.set(key, playable);
    return playable;
  }

  cyclePlaybackRate(): void {
    const index = PLAYBACK_RATES.indexOf(this.playbackRate() as (typeof PLAYBACK_RATES)[number]);
    const rate = PLAYBACK_RATES[(index + 1) % PLAYBACK_RATES.length];
    this.playbackRate.set(rate);
    const video = this.videoEl?.nativeElement;
    if (video) {
      video.playbackRate = rate;
    }
  }

  // ---- Download / print ---------------------------------------------------------------------
  download(): void {
    const item = this.state.activeItem();
    if (!item) {
      return;
    }
    void this.downloadSvc
      .download(item.url, item.name)
      .then(() => this.downloaded.emit(item))
      .catch((cause) => this.emitError('network', 'Download failed.', cause));
  }

  print(): void {
    const item = this.state.activeItem();
    if (!item || item.type !== 'image') {
      return;
    }
    const win = window.open('', '_blank', 'noopener,noreferrer');
    if (!win) {
      this.emitError('unknown', 'Unable to open the print window (popup blocked).');
      return;
    }
    const doc = win.document;
    doc.title = item.name;
    const style = doc.createElement('style');
    style.textContent = '*{margin:0}img{max-width:100%;height:auto;display:block;margin:auto}';
    doc.head.appendChild(style);
    const img = doc.createElement('img');
    img.alt = item.name;
    img.onload = () => {
      win.focus();
      win.print();
    };
    // Don't leave an empty popup orphaned if the image can't load (404 / CORS).
    img.onerror = () => {
      win.close();
      this.emitError('render', `Unable to print "${item.name}".`);
    };
    img.src = item.url;
    doc.body.appendChild(img);
    this.printed.emit(item);
  }

  // ---- Fullscreen ---------------------------------------------------------------------------
  toggleFullscreen(): void {
    const root = this.lightbox?.nativeElement;
    if (!root) {
      return;
    }
    if (!document.fullscreenElement) {
      void root.requestFullscreen?.().catch(() => undefined);
    } else {
      this.exitFullscreen();
    }
  }

  private exitFullscreen(): void {
    if (document.fullscreenElement) {
      void document.exitFullscreen?.();
    }
  }

  // ---- Pointer: pan + pinch -----------------------------------------------------------------
  onPointerDown(event: PointerEvent): void {
    if (!this.state.isImageActive()) {
      this.swipeStartX = event.clientX;
      return;
    }
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointers.size === 2) {
      this.pinchStartDistance = this.pointerDistance();
      this.pinchStartZoom = this.state.zoom();
    } else if (this.state.zoom() > 100) {
      this.panning = true;
      this.panStart = { x: event.clientX, y: event.clientY, ox: this.panX(), oy: this.panY() };
    } else {
      this.swipeStartX = event.clientX;
    }
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.pointers.has(event.pointerId)) {
      return;
    }
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointers.size === 2 && this.pinchStartDistance > 0) {
      const ratio = this.pointerDistance() / this.pinchStartDistance;
      this.zoomTo(Math.round(this.pinchStartZoom * ratio));
    } else if (this.panning) {
      this.panX.set(this.panStart.ox + (event.clientX - this.panStart.x));
      this.panY.set(this.panStart.oy + (event.clientY - this.panStart.y));
    }
  }

  onPointerUp(event: PointerEvent): void {
    const wasSingle = this.pointers.size === 1;
    const startX = this.swipeStartX;
    this.pointers.delete(event.pointerId);
    this.panning = false;
    this.pinchStartDistance = 0;
    if (
      this.enableSwipe &&
      wasSingle &&
      this.state.zoom() <= 100 &&
      Math.abs(event.clientX - startX) > 60
    ) {
      if (event.clientX < startX) {
        this.next();
      } else {
        this.previous();
      }
    }
  }

  onDoubleClick(): void {
    if (!this.state.isImageActive()) {
      return;
    }
    if (this.state.zoom() > 100) {
      this.fitToScreen();
    } else {
      this.zoomTo(200);
    }
  }

  onWheel(event: WheelEvent): void {
    if (!this.enableWheelZoom || !this.state.isImageActive()) {
      return;
    }
    event.preventDefault();
    if (event.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  private resetPan(): void {
    this.panX.set(0);
    this.panY.set(0);
  }

  private pointerDistance(): number {
    const [a, b] = [...this.pointers.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  // ---- Host events --------------------------------------------------------------------------
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.enableKeyboardNavigation || !this.state.lightboxOpen()) {
      return;
    }
    switch (event.key) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowRight':
        this.next();
        break;
      case 'ArrowLeft':
        this.previous();
        break;
      case '+':
      case '=':
        if (this.state.isImageActive()) {
          this.zoomIn();
        }
        break;
      case '-':
        if (this.state.isImageActive()) {
          this.zoomOut();
        }
        break;
      case '0':
        this.fitToScreen();
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    this.state.setFullscreen(document.fullscreenElement === this.lightbox?.nativeElement);
  }

  // ---- Track-by -----------------------------------------------------------------------------
  trackById(_index: number, file: MediaFile): string {
    return file.id;
  }

  trackByExt(_index: number, ext: string): string {
    return ext;
  }

  // ---- Helpers ------------------------------------------------------------------------------
  private focusLightbox(): void {
    this.lastFocused = document.activeElement as HTMLElement;
    this.lightbox?.nativeElement.focus();
  }

  private setupThumbObserver(): void {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) {
              continue;
            }
            const id = (entry.target as HTMLElement).dataset['videoId'];
            const url = (entry.target as HTMLElement).dataset['videoUrl'];
            if (id && url && !this.videoThumbs()[id]) {
              this.captureThumb(id, url);
            }
            this.observer?.unobserve(entry.target);
          }
        },
        { rootMargin: '200px' }
      );
    });
  }

  private observeCards(): void {
    if (!this.observer) {
      return;
    }
    this.cards?.forEach((ref) => {
      if (ref.nativeElement.dataset['videoId']) {
        this.observer?.observe(ref.nativeElement);
      }
    });
  }

  private captureThumb(id: string, url: string): void {
    this.thumbnails
      .captureVideoFrame(url)
      .then((frame) =>
        this.zone.run(() => this.videoThumbs.update((map) => ({ ...map, [id]: frame })))
      )
      .catch(() => undefined);
  }

  private videoMime(extension: string): string {
    switch (extension.toLowerCase()) {
      case 'mp4':
        return 'video/mp4';
      case 'webm':
        return 'video/webm';
      case 'ogg':
      case 'ogv':
        return 'video/ogg';
      case 'mov':
        return 'video/quicktime';
      default:
        return '';
    }
  }

  private emitError(code: MediaViewerError['code'], message: string, cause?: unknown): void {
    const error: MediaViewerError = { code, message, cause };
    this.state.setMediaError(error);
    this.errored.emit(error);
  }

  // ---- Display formatting -------------------------------------------------------------------
  formatSize(bytes: number): string {
    if (!bytes || bytes < 0) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    const exponent = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    const value = bytes / Math.pow(1024, exponent);
    return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
  }

  formatDuration(seconds?: number): string {
    if (!seconds || seconds <= 0) {
      return '';
    }
    const total = Math.round(seconds);
    const minutes = Math.floor(total / 60);
    const secs = total % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }
}
